import React, { useState } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import {
    Typography, Button, List, ListItem, ListItemText,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, CircularProgress, Card,
    Tabs, Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SECTION1_FIELDS = [
    { key: 'AssessmentReason', label: 'Assessment Reason', required: true },
    { key: 'AbilityToHear', label: 'Ability To Hear' },
    { key: 'AbilityToSee', label: 'Ability To See' },
];
const SECTION2_FIELDS = [
    { key: 'HealthLiteracy', label: 'Health Literacy' },
    { key: 'CognitiveFunctioning', label: 'Cognitive Functioning' },
    { key: 'Confused', label: 'Confused' },
    { key: 'Anxious', label: 'Anxious' },
    { key: 'Isolation', label: 'Isolation' },
    { key: 'MemoryDeficit', label: 'Memory Deficit' },
];
const SECTION3_FIELDS = [
    { key: 'ImpairedDM', label: 'Impaired DM' },
    { key: 'VerbalDisruption', label: 'Verbal Disruption' },
    { key: 'PhysicalAggression', label: 'Physical Aggression' },
    { key: 'DisruptiveBehavior', label: 'Disruptive Behavior' },
    { key: 'DelusionalBehavior', label: 'Delusional Behavior' },
    { key: 'RiskForHospitalization', label: 'Risk For Hospitalization' },
];
const ALL_FIELDS = [...SECTION1_FIELDS, ...SECTION2_FIELDS, ...SECTION3_FIELDS];

const emptyForm = () => {
    const f = {};
    ALL_FIELDS.forEach(field => { f[field.key] = ''; });
    return f;
};

function AssessmentDetailModal({ assessment, open, onClose }) {
    if (!assessment) return null;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{assessment.AssessmentReason || '(no reason given)'}</DialogTitle>
            <DialogContent>
                {assessment.addedOn && (
                    <Typography variant="body2" style={{ marginBottom: 8 }}>
                        <span style={{ color: '#666' }}>Added On:</span> {moment(assessment.addedOn).format('MM/DD/YYYY')}
                    </Typography>
                )}
                {ALL_FIELDS.filter(f => f.key !== 'AssessmentReason').map(f => assessment[f.key] ? (
                    <Typography key={f.key} variant="body2" style={{ marginBottom: 4 }}>
                        <span style={{ color: '#666' }}>{f.label}:</span> {assessment[f.key]}
                    </Typography>
                ) : null)}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function AssessmentSection({ assessments, customer_id, addedBy, onAdd, onDelete }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailAssessment, setDetailAssessment] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        setDeleting(id);
        try {
            await axios.delete(APIURL + `/customer_assessment/${id}`);
            onDelete(id);
        } catch (err) {
            console.error(err);
        }
        setDeleting(null);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!form.AssessmentReason?.trim()) {
            alert('Assessment Reason is required.');
            return;
        }
        setSaving(true);
        try {
            const payload = { customer_id, addedBy, ...form };
            const res = await axios.post(APIURL + `/customer_assessment`, payload);
            onAdd({
                customer_assessment_id: res.data?.insertId || Date.now(),
                ...payload,
                addedOn: new Date().toISOString(),
            });
            setForm(emptyForm());
            setDialogOpen(false);
        } catch (err) {
            console.error(err);
        }
        setSaving(false);
    };

    const renderFields = (fields) => fields.map(f => (
        <TextField
            key={f.key}
            label={f.label}
            fullWidth
            size="small"
            variant="outlined"
            value={form[f.key]}
            onChange={e => handleChange(f.key, e.target.value)}
            required={!!f.required}
            style={{ marginTop: 10 }}
        />
    ));

    return (
        <Card elevation={3} style={{ marginBottom: 5, padding: 12, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Typography variant="subtitle1" fontWeight="bold">ASSESSMENTS</Typography>
                <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => { setForm(emptyForm()); setActiveTab(0); setDialogOpen(true); }}>
                    Add
                </Button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {assessments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No assessments recorded.</Typography>
                ) : (
                    <List dense disablePadding style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {assessments.map((a, i) => (
                            <ListItem
                                key={a.customer_assessment_id || i}
                                disablePadding
                                onClick={() => setDetailAssessment(a)}
                                sx={{
                                    px: 1,
                                    borderRadius: 1,
                                    border: '1px solid #e8e8e8',
                                    cursor: 'pointer',
                                    bgcolor: '#fafafa',
                                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                                    '&:hover': { bgcolor: '#EBF5FB', borderColor: '#90CAF9' },
                                }}
                            >
                                <ListItemText
                                    primary={a.AssessmentReason || '(no reason given)'}
                                    secondary={a.addedOn ? moment(a.addedOn).format('MM/DD/YYYY') : ''}
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', mr: 0.5 }} />
                                <IconButton size="small" onClick={(e) => handleDelete(e, a.customer_assessment_id)} disabled={deleting === a.customer_assessment_id}>
                                    {deleting === a.customer_assessment_id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" color="error" />}
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </div>

            <AssessmentDetailModal assessment={detailAssessment} open={!!detailAssessment} onClose={() => setDetailAssessment(null)} />

            <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setActiveTab(0); }} maxWidth="sm" fullWidth>
                <DialogTitle>Add Assessment</DialogTitle>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} style={{ borderBottom: '1px solid #e0e0e0', paddingLeft: 16 }}>
                    <Tab label="Section 1" value={0} />
                    <Tab label="Section 2" value={1} />
                    <Tab label="Section 3" value={2} />
                </Tabs>
                <DialogContent>
                    {activeTab === 0 && renderFields(SECTION1_FIELDS)}
                    {activeTab === 1 && renderFields(SECTION2_FIELDS)}
                    {activeTab === 2 && renderFields(SECTION3_FIELDS)}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDialogOpen(false); setActiveTab(0); }}>Cancel</Button>
                    {activeTab < 2
                        ? <Button variant="outlined" onClick={() => setActiveTab(t => t + 1)}>Next</Button>
                        : <Button onClick={handleSave} variant="contained" disabled={saving}>
                            {saving ? <CircularProgress size={20} /> : 'Add Assessment'}
                        </Button>
                    }
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default AssessmentSection;
